// useSelectedDevice.js
//
// Persists the user's currently-selected device across navigation between
// Dashboard, Trends, and Settings — and across page reloads.
//
// Stores the bare deviceId (e.g. "WT-0001"). All v8 schema reads/writes
// are keyed by this directly:
//   devices/{deviceId}                       — device data
//   users/{uid}/devices/{deviceId}           — claim record
// so callers can use the returned value as-is, no composition needed.
//
// Storage key is per-user so multiple sign-ins on a shared browser don't
// stomp each other.
//
// Falls back gracefully to devices[0].deviceId if the saved id is no longer
// in the user's claimed devices list (e.g. they removed it on another tab).

import { useState, useEffect, useCallback } from 'react';

function storageKey(uid) {
  return `soko.selectedDevice.${uid || 'anon'}`;
}

export default function useSelectedDevice(user, devices) {
  const [selected, setSelectedState] = useState(null);

  // Initial selection + reconciliation on devices/user change.
  // Runs whenever the claimed-devices list updates.
  useEffect(() => {
    if (!user || !devices || devices.length === 0) {
      setSelectedState(null);
      return;
    }

    const ids = devices.map(d => d.deviceId);

    // ?device=WT-0010 wins over the saved selection, once.
    //
    // A share invite links here with the tank that was just shared. Without
    // this the recipient lands on whatever device they looked at last and has
    // to hunt for the new pill, which is the opposite of what the email
    // promised. Consumed immediately -- the param is stripped from the URL so a
    // later reload or a Back press does not keep forcing the same device.
    let fromUrl = null;
    try {
      const params = new URLSearchParams(window.location.search);
      const want = (params.get('device') || '').trim().toUpperCase();
      if (want && ids.includes(want)) {
        fromUrl = want;
        params.delete('device');
        const qs = params.toString();
        window.history.replaceState({}, '',
          window.location.pathname + (qs ? `?${qs}` : '') + window.location.hash);
      }
    } catch {
      // Malformed URL or no History API — fall through to the saved value.
    }
    if (fromUrl) {
      setSelectedState(fromUrl);
      try { window.localStorage.setItem(storageKey(user.uid), fromUrl); } catch { /* non-fatal */ }
      return;
    }

    let saved = null;
    try {
      saved = window.localStorage.getItem(storageKey(user.uid));
    } catch {
      // localStorage can throw in private mode / disabled storage.
      // Fall through to first-device fallback.
    }

    if (saved && ids.includes(saved)) {
      setSelectedState(saved);
    } else {
      // No saved value, or saved value points at a device the user no longer
      // has claimed. Default to first device.
      setSelectedState(devices[0].deviceId);
    }
  }, [user, devices]);

  // Wrapped setter — writes to localStorage too.
  const setSelected = useCallback((deviceId) => {
    setSelectedState(deviceId);
    if (!user) return;
    try {
      if (deviceId) {
        window.localStorage.setItem(storageKey(user.uid), deviceId);
      } else {
        window.localStorage.removeItem(storageKey(user.uid));
      }
    } catch {
      // localStorage write failed — selection still works in memory for
      // this session, just won't persist. Acceptable degradation.
    }
  }, [user]);

  return [selected, setSelected];
}